import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { Modal } from '../../components/ui/Modal';
import { StatusDot } from '../../components/ui/StatusDot';
import { ICONO_ESTADO_SENSOR } from '../../components/domain/iconosDominio';
import type { Sensor } from '../../../domain/entities/Sensor';
import { UmbralAnalogico, UmbralDigital } from '../../../domain/entities/Umbral';
import { formatearAntiguedad, formatearFechaHora } from '../../utils/formato';
import estilos from './Monitoreo.module.css';

interface DetalleSensorProps {
  sensor: Sensor | null;
  /** Conexión del nodo, `null` si no puede saberse. */
  nodoEnLinea?: boolean | null;
  onCerrar: () => void;
}

interface Dato {
  etiqueta: string;
  valor: string;
}

/** Segundos en un texto que se lea de un vistazo. */
function comoDuracion(segundos: number): string {
  if (segundos <= 0) return 'Inmediato';
  if (segundos < 60) return `${segundos} s`;

  const minutos = Math.floor(segundos / 60);
  const resto = segundos % 60;
  return resto === 0 ? `${minutos} min` : `${minutos} min ${resto} s`;
}

/**
 * Qué vigila el umbral, contado en una frase.
 *
 * Los números sueltos —«mín 10, máx 40»— obligan a interpretarlos cada vez.
 * Dicho como una condición se entiende sin pensar.
 */
function describirUmbral(sensor: Sensor): Dato[] {
  const umbral = sensor.umbral;
  if (!umbral || !umbral.estaConfigurado) return [];

  const unidad = sensor.unidadMostrable ? ` ${sensor.unidadMostrable}` : '';
  const datos: Dato[] = [];

  if (umbral instanceof UmbralAnalogico) {
    const { valorMinimo: min, valorMaximo: max } = umbral;

    datos.push({
      etiqueta: 'Salta cuando',
      valor:
        min !== null && max !== null
          ? `El valor sale de ${min}${unidad} – ${max}${unidad}`
          : min !== null
            ? `El valor baja de ${min}${unidad}`
            : max !== null
              ? `El valor sube de ${max}${unidad}`
              : 'Sin límites definidos',
    });

    if (umbral.histeresis !== null && umbral.histeresis > 0) {
      datos.push({
        etiqueta: 'Margen de vuelta',
        valor: `${umbral.histeresis}${unidad} — evita que salte y se calme sin parar rondando el límite`,
      });
    }

    if (umbral.inhibicionArranqueSegundos > 0) {
      datos.push({
        etiqueta: 'Espera tras arrancar',
        valor: comoDuracion(umbral.inhibicionArranqueSegundos),
      });
    }
  }

  if (umbral instanceof UmbralDigital) {
    datos.push({
      etiqueta: 'Salta cuando',
      valor:
        umbral.estadoEsperado === null
          ? 'Sin estado esperado definido'
          : `Deja de estar ${umbral.estadoEsperado ? 'encendido' : 'apagado'}`,
    });

    if (umbral.horarios.length > 0) {
      datos.push({
        etiqueta: 'Sólo vigila',
        valor: `En ${umbral.horarios.length} franja${umbral.horarios.length === 1 ? '' : 's'} horaria${umbral.horarios.length === 1 ? '' : 's'}`,
      });
    }
  }

  datos.push({ etiqueta: 'Gravedad', valor: umbral.criticidad.etiqueta });
  datos.push({ etiqueta: 'Retardo antes de avisar', valor: comoDuracion(umbral.retardoSegundos) });
  datos.push({ etiqueta: 'Avisa a los contactos', valor: umbral.notificaciones ? 'Sí' : 'No' });

  if (umbral.retencion) {
    datos.push({
      etiqueta: 'Retenida',
      valor: 'Sigue activa aunque el valor vuelva a la normalidad, hasta que alguien la resuelva',
    });
  }

  if (umbral.actuadoresVinculados.length > 0) {
    datos.push({
      etiqueta: 'Acciona',
      valor: `${umbral.actuadoresVinculados.length} actuador${umbral.actuadoresVinculados.length === 1 ? '' : 'es'}`,
    });
  }

  return datos;
}

/**
 * Ficha de un sensor.
 *
 * Reúne lo que hay repartido por la pantalla —última lectura, ubicación y qué
 * condición lo hace saltar— para no tener que ir a Umbrales a saber por qué un
 * sensor está en rojo.
 */
export function DetalleSensor({ sensor, nodoEnLinea, onCerrar }: DetalleSensorProps) {
  if (!sensor) return null;

  const estado = sensor.estadoCon(nodoEnLinea);
  const desconectado = nodoEnLinea === false;

  const identificacion: Dato[] = [
    { etiqueta: 'Tipo', valor: sensor.tipoNombre ?? (sensor.esDigital ? 'Estado' : 'Medida') },
    { etiqueta: 'Categoría', valor: sensor.dominio.etiqueta },
    { etiqueta: 'Cliente', valor: sensor.clienteNombre ?? '—' },
    { etiqueta: 'Área', valor: sensor.areaNombre ?? '—' },
    { etiqueta: 'Nodo', valor: sensor.nodoNombre ?? 'Sin nodo asignado' },
    { etiqueta: 'Código de envío', valor: sensor.codigoEnvio ?? '—' },
    { etiqueta: 'Identificador', valor: String(sensor.id) },
  ];

  const lectura: Dato[] = [
    {
      etiqueta: 'Última lectura',
      valor: sensor.lectura?.timestamp
        ? `${formatearFechaHora(sensor.lectura.timestamp)} (${formatearAntiguedad(sensor.lectura.timestamp)})`
        : 'Todavía no ha reportado',
    },
    { etiqueta: 'Estado del sensor', valor: sensor.activo ? 'Activo' : 'Dado de baja' },
  ];

  if (nodoEnLinea !== null && nodoEnLinea !== undefined) {
    lectura.push({
      etiqueta: 'Conexión del nodo',
      valor: nodoEnLinea ? 'En línea' : 'Sin latido',
    });
  }

  const umbral = describirUmbral(sensor);

  return (
    <Modal
      abierto={sensor !== null}
      onCerrar={onCerrar}
      titulo={sensor.nombre}
      descripcion={[sensor.areaNombre, sensor.nodoNombre].filter(Boolean).join(' · ')}
      pie={
        <Button variante="secundario" onClick={onCerrar}>
          Cerrar
        </Button>
      }
    >
      <div className={estilos.detalle}>
        <div
          className={estilos.detalleValor}
          style={{ background: `color-mix(in srgb, ${estado.color} 10%, transparent)` }}
        >
          <Icon nombre={ICONO_ESTADO_SENSOR[estado.valor]} tamano={28} color={estado.color} />

          <div className={estilos.detalleValorTexto}>
            {sensor.esDigital && sensor.tieneLectura ? (
              <StatusDot
                color={estado.color}
                encendido={sensor.lectura?.esEncendido}
                texto={sensor.valorNumericoMostrable}
                tamano={12}
                etiqueta={`${sensor.nombre}: ${sensor.valorNumericoMostrable}`}
              />
            ) : (
              <span className={estilos.detalleNumero}>
                {sensor.valorNumericoMostrable}
                {sensor.unidadMostrable && (
                  <span className={estilos.valorUnidad}>{sensor.unidadMostrable}</span>
                )}
              </span>
            )}

            <Badge color={estado.color}>{estado.etiqueta}</Badge>
          </div>
        </div>

        {desconectado && (
          <p className={estilos.detalleAviso}>
            <Icon nombre="errorContorno" tamano={15} />
            El nodo está desconectado: este valor es el último que llegó, no el de ahora.
          </p>
        )}

        <SeccionDatos titulo="Qué hace saltar la alarma" datos={umbral} />
        <SeccionDatos titulo="Lectura" datos={lectura} />
        <SeccionDatos titulo="Identificación" datos={identificacion} />

        {umbral.length === 0 && (
          <p className={estilos.detalleAviso}>
            <Icon nombre="info" tamano={15} />
            Este sensor no tiene umbral configurado: se registra su valor pero nunca generará una
            alarma.
          </p>
        )}
      </div>
    </Modal>
  );
}

function SeccionDatos({ titulo, datos }: { titulo: string; datos: Dato[] }) {
  if (datos.length === 0) return null;

  return (
    <section className={estilos.detalleSeccion}>
      <h3 className={estilos.detalleTitulo}>{titulo}</h3>
      <dl className={estilos.detalleLista}>
        {datos.map((d) => (
          <div key={d.etiqueta} className={estilos.detalleFila}>
            <dt className={estilos.detalleEtiqueta}>{d.etiqueta}</dt>
            <dd className={estilos.detalleDato}>{d.valor}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
